import React, { useState, useEffect } from 'react';
import { Package2, Search, MapPin, CalendarClock, Truck, CheckCircle2, Circle, Package, IndianRupee, Clock, Phone, User, DollarSign, Box, MapPinned, Calendar } from 'lucide-react';
import NavBar from '../../../../component/b2c/navBar/navBar';
import Sidebar from '../../../../component/b2c/sidebar/sidebar';
import { useMyContext } from '../../../../context/MyContext';
import { AlertCircle, ClipboardList, PackageSearch } from 'lucide-react';
import '../../../../css/custom-scrollbar.css';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Base_Url, track } from '../../../../config/config';
import FullPageLoader from '../../../../component/fullPageLoader/fullPageLoader';


const OrderTracking = () => {
  const [isPageLoading, setIsPageLoading] = useState(false)
  const [waybillNumber, setWaybillNumber] = useState('');
  const [orderTrackingInfo, setOrderTrackingInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { isDarkMode, isToggleSidebar, orderid, setorderId } = useMyContext();

  useEffect(() => {
    if (orderid) {
      handleTrack();
    }
  }, [orderid]);


  const handleTrack = async () => {
    // Get the tracking number - prioritize waybillNumber if it exists, otherwise use orderid
    const trackingNumber = (waybillNumber || orderid)?.toString().trim();

    if (!trackingNumber) {
      setError('Please enter a valid order ID');
      setOrderTrackingInfo(null);
      return;
    }


    setIsLoading(true);
    setIsPageLoading(true);
    setError('');

    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');

    try {
      const requestPayload = {
        order_id: trackingNumber, // Use the determined tracking number
        shipment_type: 'Forward'
      };
      const response = await axios.post(Base_Url + track, requestPayload, {
        headers: {
          'token': token,
          'cus_id': cus_id,
          'Content-Type': 'application/json'
        }
      });
      if (response.data.status === "SUCCESS") {
        setOrderTrackingInfo(response.data.data.ShipmentData[0].Shipment);
        setError('');
        setIsPageLoading(false);
        // Only clear orderid from context if we successfully used it
        if (orderid && !waybillNumber) {
          setorderId(null);
        }
      }
      else {
        setError(response.data.message);
        setIsPageLoading(false);
      }
    } catch (err) {
      console.error('Tracking error:', err.response?.data || err.message); // Debug log
      setError('Failed to fetch tracking information. Please try again.');
      setOrderTrackingInfo(null);
      setIsPageLoading(false);
    } finally {
      setIsLoading(false);
      setIsPageLoading(false);
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return "Not Available";
    return new Date(dateString).toLocaleString();
  };

  const DetailRow = ({ label, value, icon: Icon }) => (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
      <div className="flex items-center space-x-2">
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
        <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
      </div>
      <span className="text-sm font-medium dark:text-white">{value || "Not Available"}</span>
    </div>
  );

  const getStepIcon = (scan, isLatest) => {
    const status = scan.ScanDetail.Scan.toLowerCase() || scan?.ScanDetail?.ScanType;
    ;
    if (status) {
      return <CheckCircle2 className="w-6 h-6 text-green-500 " />;
    }
    if (isLatest) {
      return (
        <div className="relative inline-flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 animate-pulse">
          <Truck className="w-4 h-4 text-purple-600" />
        </div>
      );
    }
    return <Circle className="w-6 h-6 text-gray-400" />;
  };

  const DeliveryInfoPanel = () => (
    <div className="space-y-6">
      {/* Shipment Overview Card */}
      <div className="bg-white dark:bg-darkmodeColor rounded-]lg shadow-lg">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold dark:text-white flex items-center gap-2">
            <Package2 className="w-5 h-5 text-purple-600" />
            Shipment Overview
          </h2>
        </div>
        <div className="p-4 space-y-3">
          <DetailRow label="AWB Number" value={orderTrackingInfo?.AWB} icon={Package} />
          <DetailRow label="Order ID" value={orderTrackingInfo?.ReferenceNo} icon={Package2} />
          <DetailRow label="Order Type" value={orderTrackingInfo?.OrderType} icon={Box} />
          <DetailRow label="Invoice Amount" value={`₹${orderTrackingInfo?.InvoiceAmount || 0}`} icon={IndianRupee} />
          <DetailRow label="COD Amount" value={`₹${orderTrackingInfo?.CODAmount || 0}`} icon={IndianRupee} />
          <DetailRow label="Quantity" value={orderTrackingInfo?.Quantity} icon={Box} />
        </div>
      </div>

      {/* Location Details */}
      <div className="bg-white dark:bg-darkmodeColor rounded-lg shadow-lg">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold dark:text-white flex items-center gap-2">
            <MapPinned className="w-5 h-5 text-blue-600" />
            Location Details
          </h2>
        </div>
        <div className="p-4 space-y-3">
          <DetailRow label="Origin" value={orderTrackingInfo?.Origin} icon={MapPin} />
          <DetailRow label="Destination" value={orderTrackingInfo?.Destination || "Not specified"} icon={MapPin} />
          <DetailRow label="Pickup Location" value={orderTrackingInfo?.PickupLocation} icon={MapPin} />
        </div>
      </div>

      {/* Consignee Details */}
      <div className="bg-white dark:bg-darkmodeColor rounded-lg shadow-lg">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold dark:text-white flex items-center gap-2">
            <User className="w-5 h-5 text-green-600" />
            Consignee Details
          </h2>
        </div>
        <div className="p-4 space-y-3">
          {orderTrackingInfo?.Consignee && (
            <>
              <DetailRow label="Name" value={orderTrackingInfo.Consignee.Name} icon={User} />
              <DetailRow label="City" value={orderTrackingInfo.Consignee.City || orderTrackingInfo.Consignee.State} icon={MapPin} />
              <DetailRow label="Pin Code" value={orderTrackingInfo.Consignee.PinCode} icon={MapPin} />
              <DetailRow label="Country" value={orderTrackingInfo.Consignee.Country} icon={MapPin} />
            </>
          )}
        </div>
      </div>

      {/* Dates Information */}
      <div className="bg-white dark:bg-darkmodeColor rounded-lg shadow-lg">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold dark:text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-orange-600" />
            Important Dates
          </h2>
        </div>
        <div className="p-4 space-y-3">
          <DetailRow label="Pickup Date" value={formatDate(orderTrackingInfo?.PickUpDate)} icon={Calendar} />
          <DetailRow label="Expected Delivery" value={formatDate(orderTrackingInfo?.ExpectedDeliveryDate)} icon={Calendar} />
          <DetailRow label="First Attempt Date" value={formatDate(orderTrackingInfo?.FirstAttemptDate)} icon={Calendar} />
        </div>
      </div>

      {/* Status Banner */}
      <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
        <div className="flex items-center space-x-3 mb-2">
          <Clock className="w-5 h-5 text-purple-600" />
          <h3 className="font-medium dark:text-white">Current Status</h3>
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium text-purple-600 dark:text-purple-400">
            {orderTrackingInfo?.Status?.Status}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-3 00">
            {orderTrackingInfo?.Status?.Instructions}
          </p>
          <p className="text-sm text-gray-500">
            Last Updated: {formatDate(orderTrackingInfo?.Status?.StatusDateTime)}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-gray-50'}`}>
      <div className="z-10">
        <NavBar />
      </div>
      <div className="flex flex-1 overflow-hidden">
        {isToggleSidebar && (
          <div className="fixed z-20 h-full w-64 md:relative duration-300 bg-gray-200 dark:bg-darkmodeColor dark:text-white md:bg-transparent mt-4 md:w-64 md:z-20"
            style={window.innerWidth < 768 ? { top: "113px", height: "calc(100vh - 115px)", overflowY: 'auto' } : {}}>
            <Sidebar />
          </div>
        )}

        <FullPageLoader
          isLoading={isPageLoading}
          message="Processing your request..."
        />

        <div className="flex-1 p-4 md:p-8 overflow-auto">
          {/* <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl mx-auto"> */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 max-w-7xl mx-auto">
            {/* Main Tracking Section */}
            <div className="lg:col-span-2 bg-white dark:bg-darkmodeColor shadow-lg rounded-lg">
              <div className="p-6">
                <div className="flex items-center space-x-2 mb-6">
                  <Package2 className="w-6 h-6 text-purple-600" />
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Track Your Shipment</h1>
                </div>
                <div className="space-y-4">
                  {/* Search section and tracking timeline remain the same */}
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      placeholder="Enter Order ID"
                      // value={orderid}
                      onChange={(e) => setWaybillNumber(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                    />
                    <button
                      onClick={handleTrack}
                      disabled={isLoading}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 flex items-center space-x-2"
                    >
                      {isLoading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Search className="w-4 h-4" />
                          <span>Track</span>
                        </>
                      )}
                    </button>
                  </div>
                  {error && (
                    <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 dark:bg-red-900/20 dark:text-red-400">
                      {error}
                    </div>
                  )}
                  {orderTrackingInfo && (
                    <div className="space-y-6 mt-8">
                      {orderTrackingInfo.Scans?.map((scan, index) => (
                        <div key={index} className="relative">
                          <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0">
                              {getStepIcon(scan, index === 0)}
                            </div>
                            <div className="flex-1 space-y-1">
                              <p className="font-medium dark:text-white">
                                {scan.ScanDetail.Instructions}
                              </p>
                              <p className="text-xs text-gray-400">
                                {formatDate(scan.ScanDetail.StatusDateTime)}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {scan.ScanDetail.ScannedLocation}
                              </p>
                            </div>
                          </div>
                          {index < orderTrackingInfo.Scans.length - 1 && (
                            <div className="absolute left-3 top-8 w-0.5 h-16 bg-gray-200 dark:bg-gray-700" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Info Panel */}
            <div className="lg:col-span-3">
              <DeliveryInfoPanel />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


export default OrderTracking;
